import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * 149-cw-r193: SearchableMultiSelect — versi multi-pilih dari SearchableSelect
 * (user request: filter petty cash bisa pilih BEBERAPA project sekaligus).
 * Checkbox per opsi, popover tetap terbuka saat toggle, baris "Semua" di atas
 * untuk reset. values=[] berarti "semua" (tanpa filter).
 *
 * Props:
 *  - values: string[]
 *  - onChange: (values: string[]) => void
 *  - options: [{ value, label }]
 *  - allLabel, searchPlaceholder, emptyText, disabled, testid, widthClass
 */
export default function SearchableMultiSelect({
  values,
  onChange,
  options = [],
  allLabel = "Semua",
  searchPlaceholder = "Ketik untuk mencari…",
  emptyText = "Tidak ada hasil yang cocok",
  disabled = false,
  testid,
  widthClass = "w-72",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  const selectedSet = useMemo(() => new Set(values || []), [values]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return options;
    return options.filter((o) => (o.label || "").toLowerCase().includes(query));
  }, [options, q]);

  useEffect(() => {
    if (open) {
      setQ("");
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const toggle = (v) => {
    const next = new Set(selectedSet);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange([...next]);
  };

  const label =
    selectedSet.size === 0
      ? allLabel
      : selectedSet.size === 1
        ? (options.find((o) => o.value === values[0])?.label || allLabel)
        : `${selectedSet.size} dipilih`;

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testid}
          className="h-9 px-3 border border-slate-300 bg-white text-xs inline-flex items-center gap-2 hover:border-[#0B1D3A] disabled:opacity-50 disabled:cursor-not-allowed rounded-none"
        >
          <span className={`truncate max-w-[240px] ${selectedSet.size ? "text-slate-800" : "text-slate-500"}`}>
            {label}
          </span>
          <ChevronDown size={12} className="shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className={`p-0 rounded-none ${widthClass}`} onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="flex items-center gap-2 border-b border-slate-200 px-2">
          <Search size={13} className="text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            data-testid={testid ? `${testid}-search` : undefined}
            className="flex-1 h-9 text-xs bg-transparent focus:outline-none placeholder:text-slate-400"
          />
        </div>
        <div
          className="max-h-[60vh] overflow-y-auto overscroll-contain"
          data-testid={testid ? `${testid}-list` : undefined}
          // 149-cw-r196: wheel fix di dalam Dialog modal (react-remove-scroll).
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            data-testid={testid ? `${testid}-all` : undefined}
            onClick={() => { onChange([]); setOpen(false); }}
            className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 border-b border-slate-200 font-semibold transition-colors ${
              selectedSet.size === 0 ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <Check size={12} className={`shrink-0 ${selectedSet.size === 0 ? "opacity-100" : "opacity-0"}`} />
            <span className="truncate">{allLabel}</span>
          </button>
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-[11px] text-slate-400 italic text-center">{emptyText}</div>
          )}
          {filtered.map((o) => {
            const on = selectedSet.has(o.value);
            return (
              <button
                key={o.value}
                type="button"
                data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                onClick={() => toggle(o.value)}
                className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 border-b border-slate-100 last:border-0 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <span className={`shrink-0 w-3.5 h-3.5 border inline-flex items-center justify-center ${on ? "bg-[#0B1D3A] border-[#0B1D3A] text-white" : "border-slate-300 text-transparent"}`}>
                  <Check size={10} />
                </span>
                <span className="truncate">{o.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
